import { c as _c } from "react/compiler-runtime";
import type React from "react";
import { LinkIcon, PlayIcon } from "@heroicons/react/24/outline";
import { FallbackImage } from "@/components/common/FallbackImage";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import type { PreviewItem } from "./types";
import { getMediaAspectRatio, getPreviewImageUrl } from "./utils";
const PreviewMediaTile: React.FC<{
  readonly item: Extract<PreviewItem, {
    readonly kind: "media";
  }>;
}> = t0 => {
  const $ = _c(35);
  const {
    item
  } = t0;
  const hasText = item.text !== null;
  let imageUrl;
  let isVideo;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== hasText || $[1] !== item.media || $[2] !== item.text) {
    imageUrl = item.media.imageUrl === null ? null : getPreviewImageUrl(item.media.imageUrl);
    isVideo = item.media.kind === "video";
    t4 = "tw-[contain:content] tw-[content-visibility:auto] tw-group tw-mb-2 tw-inline-block tw-w-full tw-break-inside-avoid tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-white/[0.06] tw-bg-[#1A1A20] tw-align-top tw-shadow-sm tw-shadow-black/20 tw-transition-colors tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-white/[0.12] desktop-hover:hover:tw-bg-[#202028]";
    t5 = item.text ?? (isVideo ? "Curated video" : "Curated media");
    t1 = "tw-relative tw-w-full tw-bg-[#101014]";
    let t6;
    if ($[10] !== hasText || $[11] !== item.media) {
      t6 = getMediaAspectRatio(item.media, hasText);
      $[10] = hasText;
      $[11] = item.media;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    if ($[13] !== t6) {
      t2 = {
        aspectRatio: t6
      };
      $[13] = t6;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    t3 = imageUrl === null ? <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-[#101014]" aria-hidden="true"><span className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/10 tw-text-white/70 tw-ring-1 tw-ring-white/15"><PlayIcon className="tw-ml-0.5 tw-h-5 tw-w-5" /></span></div> : <FallbackImage primarySrc={getScaledImageUri(imageUrl, ImageScale.W_200_H_200)} fallbackSrc={imageUrl} alt="" fill={true} sizes="156px" loading="lazy" decoding="async" className="tw-object-cover" />;
    $[0] = hasText;
    $[1] = item.media;
    $[2] = item.text;
    $[3] = imageUrl;
    $[4] = isVideo;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    imageUrl = $[3];
    isVideo = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
  }
  let t6;
  if ($[15] !== item.mediaCount) {
    t6 = item.mediaCount > 1 && <span className="tw-absolute tw-bottom-1.5 tw-left-1.5 tw-rounded tw-bg-black/55 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-bold tw-leading-none tw-text-white/90">+{item.mediaCount - 1}</span>;
    $[15] = item.mediaCount;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== imageUrl || $[18] !== isVideo) {
    t7 = isVideo && imageUrl !== null && <PlayIcon className="tw-absolute tw-bottom-1.5 tw-right-1.5 tw-h-4 tw-w-4 tw-text-white/60" aria-hidden="true" />;
    $[17] = imageUrl;
    $[18] = isVideo;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t1 || $[21] !== t2 || $[22] !== t3 || $[23] !== t6 || $[24] !== t7) {
    t8 = <div className={t1} style={t2}>{t3}{t6}{t7}</div>;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== hasText || $[27] !== item.showLinkHint || $[28] !== item.text) {
    t9 = hasText && <div className="tw-flex tw-items-start tw-gap-1.5 tw-px-2.5 tw-py-2"><p dir="auto" className="tw-[overflow-wrap:anywhere] tw-mb-0 tw-line-clamp-2 tw-min-w-0 tw-flex-1 tw-break-words tw-text-xs tw-font-medium tw-leading-snug tw-text-zinc-300 tw-transition-colors tw-duration-300 desktop-hover:group-hover:tw-text-white">{item.text}</p>{item.showLinkHint && <LinkIcon className="tw-mt-0.5 tw-h-3 tw-w-3 tw-flex-shrink-0 tw-text-primary-300/80" aria-hidden="true" />}</div>;
    $[26] = hasText;
    $[27] = item.showLinkHint;
    $[28] = item.text;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== t4 || $[31] !== t5 || $[32] !== t8 || $[33] !== t9) {
    t10 = <div className={t4} aria-label={t5}>{t8}{t9}</div>;
    $[30] = t4;
    $[31] = t5;
    $[32] = t8;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  return t10;
};
const PreviewLinkTile: React.FC<{
  readonly item: Extract<PreviewItem, {
    readonly kind: "link";
  }>;
}> = t0 => {
  const $ = _c(9);
  const {
    item
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <LinkIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== item.host) {
    t2 = <span className="tw-mb-2 tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-bold tw-leading-none tw-text-primary-300/95">{t1}<span className="tw-min-w-0 tw-truncate">{item.host}</span></span>;
    $[1] = item.host;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== item.text) {
    t3 = <span dir="auto" className="tw-[overflow-wrap:anywhere] tw-line-clamp-2 tw-break-words tw-text-[13px] tw-font-semibold tw-leading-snug">{item.text}</span>;
    $[3] = item.text;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== item.url || $[6] !== t2 || $[7] !== t3) {
    t4 = <a href={item.url} target="_blank" rel="noreferrer" className="tw-text-primary-100 tw-[contain:content] tw-[content-visibility:auto] desktop-hover:hover:tw-text-primary-50 tw-group tw-mb-2 tw-inline-block tw-w-full tw-break-inside-avoid tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-primary-500/15 tw-bg-primary-500/5 tw-p-2.5 tw-align-top tw-no-underline tw-shadow-sm tw-shadow-black/20 tw-transition-colors tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-primary-500/30 desktop-hover:hover:tw-bg-primary-500/10">{t2}{t3}</a>;
    $[5] = item.url;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
const InlineTextLink: React.FC<{
  readonly item: Extract<PreviewItem, {
    readonly kind: "text";
  }>;
}> = t0 => {
  const $ = _c(6);
  const {
    item
  } = t0;
  if (item.inlineUrl === null || item.linkHost === null) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <LinkIcon className="tw-h-2.5 tw-w-2.5" aria-hidden="true" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== item.linkHost) {
    t2 = <span>{item.linkHost}</span>;
    $[1] = item.linkHost;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== item.inlineUrl || $[4] !== t2) {
    t3 = <a href={item.inlineUrl} target="_blank" rel="noreferrer" className="desktop-hover:hover:tw-text-primary-200 tw-ml-1 tw-inline-flex tw-items-center tw-gap-1 tw-text-primary-300 tw-no-underline">{t1}{t2}</a>;
    $[3] = item.inlineUrl;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
const PreviewTextTile: React.FC<{
  readonly item: Extract<PreviewItem, {
    readonly kind: "text";
  }>;
}> = t0 => {
  const $ = _c(12);
  const {
    item
  } = t0;
  if (item.isEmojiOnly) {
    let t1;
    if ($[0] !== item.text) {
      t1 = <div className="tw-[contain:content] tw-[content-visibility:auto] tw-group tw-mb-2 tw-inline-flex tw-min-h-20 tw-w-full tw-break-inside-avoid tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-white/[0.06] tw-bg-[#1A1A20] tw-p-3 tw-text-center tw-align-top tw-text-2xl tw-leading-none tw-text-zinc-300 tw-shadow-sm tw-shadow-black/20 tw-transition-colors tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-white/[0.12] desktop-hover:hover:tw-bg-[#202028] desktop-hover:hover:tw-text-white"><p dir="auto" className="tw-[overflow-wrap:anywhere] tw-mb-0 tw-break-words">{item.text}</p></div>;
      $[0] = item.text;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (item.isShortText) {
    let t1;
    if ($[2] !== item) {
      t1 = <InlineTextLink item={item} />;
      $[2] = item;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== item.text || $[5] !== t1) {
      t2 = <div className="tw-[contain:content] tw-[content-visibility:auto] tw-group tw-mb-2 tw-inline-flex tw-min-h-14 tw-w-full tw-break-inside-avoid tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-white/[0.06] tw-bg-[#1A1A20] tw-p-2.5 tw-text-center tw-align-top tw-text-[13px] tw-font-semibold tw-leading-snug tw-text-zinc-300 tw-shadow-sm tw-shadow-black/20 tw-transition-colors tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-white/[0.12] desktop-hover:hover:tw-bg-[#202028] desktop-hover:hover:tw-text-white"><p dir="auto" className="tw-[overflow-wrap:anywhere] tw-mb-0 tw-break-words">{item.text}{t1}</p></div>;
      $[4] = item.text;
      $[5] = t1;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  let t1;
  if ($[7] !== item) {
    t1 = <InlineTextLink item={item} />;
    $[7] = item;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== item.text || $[10] !== t1) {
    t2 = <div className="tw-[contain:content] tw-[content-visibility:auto] tw-group tw-mb-2 tw-inline-block tw-w-full tw-break-inside-avoid tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-white/[0.06] tw-bg-[#1A1A20] tw-p-2.5 tw-align-top tw-text-xs tw-font-medium tw-leading-snug tw-text-zinc-300 tw-shadow-sm tw-shadow-black/20 tw-transition-colors tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-white/[0.12] desktop-hover:hover:tw-bg-[#202028] desktop-hover:hover:tw-text-white"><p dir="auto" className="tw-[overflow-wrap:anywhere] tw-mb-0 tw-line-clamp-5 tw-break-words">{item.text}{t1}</p></div>;
    $[9] = item.text;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
export const PreviewTile: React.FC<{
  readonly item: PreviewItem;
}> = t0 => {
  const $ = _c(6);
  const {
    item
  } = t0;
  if (item.kind === "media") {
    let t1;
    if ($[0] !== item) {
      t1 = <PreviewMediaTile item={item} />;
      $[0] = item;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (item.kind === "link") {
    let t1;
    if ($[2] !== item) {
      t1 = <PreviewLinkTile item={item} />;
      $[2] = item;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== item) {
    t1 = <PreviewTextTile item={item} />;
    $[4] = item;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};